import { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import EmissionVersionFields from "../../../../../components/EmissionVersionFields/EmissionVersionFields";
import GHGEmissionService from "../../../../../core/service/GHGEmissionService";

const EmissionStatusEditor = ({ t, entity, companyId, glbRegulationId, onChange, disabled = false, validated = false }) => {
  const [emission, setEmission] = useState(entity?.emission ?? false);
  const [emissionDatabase, setEmissionDatabase] = useState(entity?.emissionDatabase || entity?.database || "");
  const [emissionVersionRef, setEmissionVersionRef] = useState(entity?.emissionVersionRef || entity?.versionRef || "");
  const [editEmissionDatabase] = useState(entity?.emissionDatabase || entity?.database || "");
  const [editEmissionVersionRef] = useState(entity?.emissionVersionRef || entity?.versionRef || "");
  const [selectedScope, setSelectedScope] = useState(entity?.scope || "");
  const [selectedCategory, setSelectedCategory] = useState(entity?.scopeCategory || "");
  const [scopeData, setScopeData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);

  useEffect(() => {
    if (!entity?.emission) return;
    const db = entity.emissionDatabase || entity.database;
    const versionRef = entity.emissionVersionRef || entity.versionRef;
    if (!db || !versionRef) return;

    GHGEmissionService.getVersionScopeDetails(versionRef, db)
      .then((response) => {
        if (response.status !== 200) return;
        const scopeList = response.data?.data ?? response.data ?? [];
        setScopeData(Array.isArray(scopeList) ? scopeList : []);
        const selectedScopeData = scopeList.find((scope) => scope.name === entity.scope);
        if (selectedScopeData?.categories) {
          setCategoryData(selectedScopeData.categories.map((cat) => cat.name));
        }
      })
      .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.id]);

  useEffect(() => {
    onChange?.({
      emission,
      scope: emission ? selectedScope : "",
      scopeCategory: emission ? selectedCategory : "",
      emissionDatabase: emission ? emissionDatabase : "",
      emissionVersionRef: emission ? emissionVersionRef : "",
      isValid: !emission || Boolean(emissionDatabase && emissionVersionRef && selectedScope && selectedCategory),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emission, selectedScope, selectedCategory, emissionDatabase, emissionVersionRef]);

  const resetEmissionScopeSelection = () => {
    setSelectedScope("");
    setSelectedCategory("");
    setScopeData([]);
    setCategoryData([]);
  };

  const handleEmissionToggle = () => {
    if (emission) {
      setEmissionDatabase("");
      setEmissionVersionRef("");
      resetEmissionScopeSelection();
    }
    setEmission(!emission);
  };

  const handleEmissionConfigChange = (config) => {
    setEmissionDatabase(config.database || "");
    setEmissionVersionRef(config.versionRef || "");
  };

  const handleScopeChange = (e) => {
    const scopeName = e.target.value;
    setSelectedScope(scopeName);
    setSelectedCategory("");
    const selectedScopeData = scopeData.find((scope) => scope.name === scopeName);
    setCategoryData(selectedScopeData?.categories?.map((cat) => cat.name) ?? []);
  };

  const isInvalid = (val) => validated && emission && !val;
  const isConfigIncomplete = emission
    && !(emissionDatabase && emissionVersionRef && selectedScope && selectedCategory);

  return (
    <div className="emission-status-editor">
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id={`emissionCheckbox-${entity?.id}`}
          checked={emission}
          onChange={handleEmissionToggle}
          disabled={disabled}
        />
        <label className="form-check-label mx-1" htmlFor={`emissionCheckbox-${entity?.id}`}>
          {t("Emission.label")}
           {/* {t(emission ? "Enabled.label" : "Disabled.label")} */}
        </label>
      </div>
      {emission && (
        <div className="emission-config-section mt-2">
          <EmissionVersionFields
            key={`${entity?.id}-${editEmissionDatabase}-${editEmissionVersionRef}`}
            companyId={companyId}
            glbRegulationId={glbRegulationId}
            editDatabase={editEmissionDatabase}
            editVersionRef={editEmissionVersionRef}
            onScopeDataChange={setScopeData}
            onScopeCategoryReset={resetEmissionScopeSelection}
            onConfigChange={handleEmissionConfigChange}
            validated={validated}
          />
          <div className="row mt-2">
            <div className="col-md-6">
              <label className="mb-1">{t("Scope.label")}</label>&nbsp;*
              <select
                className={`form-select${isInvalid(selectedScope) ? " border-danger" : ""}`}
                value={selectedScope}
                onChange={handleScopeChange}
                required
                disabled={!emissionVersionRef}
              >
                <option value="">{t("Select.label")}</option>
                {scopeData.map((scope) => (
                  <option key={scope.name} value={scope.name}>
                    {scope.name}
                  </option>
                ))}
              </select>
              {validated && isConfigIncomplete && (
                <div className="text-danger small mt-2">{t("EmissionVersionFields.scope.label")}</div>
              )}
            </div>
            <div className="col-md-6">
              <label className="mb-1">{t("Category.label")}</label>&nbsp;*
              <select
                className={`form-select${isInvalid(selectedCategory) ? " border-danger" : ""}`}
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                required
                disabled={!selectedScope}
              >
                <option value="">{t("Select.label")}</option>
                {categoryData.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              {validated && isConfigIncomplete && (
                <div className="text-danger small mt-2">{t("EmissionVersionFields.category.label")}</div>
              )}
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default withTranslation()(EmissionStatusEditor);
